import { UseInfiniteQueryResult } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

export interface UseFetchAllPagesArgs<
  TData,
  TError,
  Q extends Pick<
    UseInfiniteQueryResult<TData, TError>,
    "fetchNextPage" | "hasNextPage" | "isLoading" | "isFetched" | "isError"
  >,
> {
  infiniteQuery: Q;
  enabled?: boolean;
  maxFetches?: number;
}

export const useFetchAllPages = <
  TData,
  TError,
  Q extends Pick<
    UseInfiniteQueryResult<TData, TError>,
    "fetchNextPage" | "hasNextPage" | "isLoading" | "isFetched" | "isError"
  >,
>({
  infiniteQuery: { fetchNextPage, hasNextPage, isLoading, isFetched, isError },
  enabled = true,
  maxFetches = 5,
}: UseFetchAllPagesArgs<TData, TError, Q>) => {
  const fetchCountRef = useRef(0);
  const hasErrorRef = useRef(false);

  useEffect(() => {
    if (fetchCountRef.current >= maxFetches || hasErrorRef.current || isLoading || !isFetched || isError || !enabled)
      return;
    if (hasNextPage && enabled) {
      fetchNextPage().then((query) => {
        if (query.status === "error") {
          hasErrorRef.current = true;
        } else {
          fetchCountRef.current += 1;
        }
      });
    }
  }, [enabled, fetchNextPage, hasNextPage, isError, isFetched, isLoading, maxFetches]);
};
